import { Grid } from '~/engine/data/common/Grid';
import { centralDifferenceGradient2D } from '../scalarField2d';
import { SDF_ISO_THRESHOLD } from './sdfConstants';

/**
 * Image-intensity guided refinement of interpolated SDF slices.
 *
 * These are pure, allocation-light functions operating on a single 2D
 * SDF slice (`Uint8Array`, iso ≈ {@link SDF_ISO_THRESHOLD})
 * plus an aligned intensity slice (`Float32Array`/`ArrayLike<number>`
 * where out-of-volume samples are `NaN`). They work with any scalar
 * image modality (CT Hounsfield units, PET SUV/activity, MR arbitrary
 * units) — the algorithms are intensity-scale agnostic. They have no
 * engine, view, tool, or React dependencies and are unit-testable in
 * isolation.
 *
 * They are deliberately housed in `engine/util/algorithms/sdf` (next
 * to {@link ContourOperations} and the rest of the SDF algorithm
 * family) rather than under a specific tool, so that BOTH the
 * slice-to-slice auto-suggest tool and the explicit Interpolation
 * tool can share them without crossing the tool ↔ util layer
 * boundary.
 *
 * Two complementary correction strategies are provided, each a
 * single narrow-band pass that preserves topology (deep interior /
 * exterior pixels are never touched) and clamps per-pixel change:
 *
 *   - {@link refineSliceWithIntensityEdge} — edge / gradient based:
 *     pulls the iso toward strong intensity gradients (anatomical
 *     boundaries), gated by directional alignment with the SDF
 *     gradient.
 *   - {@link refineSliceWithIntensityRegion} — region / absolute
 *     intensity based: contracts or expands where the absolute
 *     intensity at a boundary pixel clearly matches the exterior /
 *     interior population (e.g. air vs tissue on CT, background vs
 *     uptake on PET).
 *
 * {@link extractIntensitySliceAlignedToGrid} produces the aligned
 * intensity slice these functions consume from an image's float pixel
 * buffer.
 *
 * Default parameters are tuned for CT (Hounsfield Units). For other
 * modalities, use {@link scaleRefinementOptionsForSpread} to map the
 * intensity-unit thresholds onto the image's own intensity range.
 */

// ─── Intensity-guided narrow-band refinement ──────────────────────────────────

/**
 * Tuning parameters for {@link refineSliceWithIntensityEdge}.
 * Defaults are tuned for CT data (HU ≈ −1000 to +1000) and a
 * typical ~1 mm/voxel grid. For non-CT modalities, scale the
 * intensity-unit parameters (`edgeNoiseFloor`, `edgeStrengthMax`)
 * using {@link scaleRefinementOptionsForSpread}.
 */
export type EdgeRefinementOptions = {
    /** SDF iso-value (default {@link SDF_ISO_THRESHOLD}). */
    threshold: number;
    /**
     * Half-width of the narrow band around the iso-surface to refine,
     * in **millimetres**. Pixels farther than this from the iso are
     * left untouched. Converted to SDF byte units internally using the
     * `sdfBytesPerMm` argument, so the band's physical size is
     * independent of the SDF encoding.
     */
    bandWidthMm: number;
    /**
     * `|∇I|` magnitude (per voxel) below which the gradient is
     * considered noise and no correction is applied. Default 30
     * is tuned for CT speckle noise (~10–30 HU/voxel).
     */
    edgeNoiseFloor: number;
    /**
     * `|∇I|` magnitude that maps to a full-strength correction.
     * Anything above this saturates. Default 300 is tuned for
     * strong CT interfaces (lung wall, bone–soft-tissue).
     */
    edgeStrengthMax: number;
    /**
     * Global correction scale applied per pixel per pass. Keeps
     * iterations stable; lower values yield gentler refinements.
     */
    alpha: number;
    /**
     * Maximum iso displacement per pixel per pass, in **millimetres**.
     * Converted to SDF byte units using the `sdfBytesPerMm` argument and
     * used as the per-pixel clamp; prevents a single noisy pixel from
     * flipping topology.
     */
    maxShiftMm: number;
};

export const DEFAULT_EDGE_REFINEMENT_OPTIONS: Readonly<EdgeRefinementOptions> =
    {
        threshold: SDF_ISO_THRESHOLD,
        bandWidthMm: 0.25,
        edgeNoiseFloor: 30,
        edgeStrengthMax: 300,
        alpha: 0.4,
        maxShiftMm: 0.625,
    };

/**
 * Shared epilogue of both refinement passes: for every eligible
 * pixel clamp its correction to `±maxDelta`, add it to the input SDF,
 * and write the rounded, `[0, 255]`-clamped result into `out`.
 * `sdfSlice` is read-only; `out` is mutated in place. Returns `out`
 * for chaining.
 *
 * Corrections are applied directly, with no post-hoc smoothing of the
 * delta field. Boundary smoothness is instead a property of the
 * pipeline itself: the B-spline blend supplies a globally smooth
 * prior, and each correction is small (`±maxDelta`), gated
 * (gradient/region alignment + confidence), and confined to a narrow
 * band — so the iso the corrections produce stays smooth without
 * blunting the legitimately sharp edges intensity refinement is meant
 * to snap to.
 */
function commitDeltaField(
    sdfSlice: Uint8Array,
    out: Uint8Array,
    deltaField: Float32Array,
    eligible: Uint8Array,
    maxDelta: number
): Uint8Array {
    for (let i = 0; i < deltaField.length; i++) {
        if (eligible[i] === 0) {
            continue;
        }
        let delta = deltaField[i];
        if (delta > maxDelta) {
            delta = maxDelta;
        } else if (delta < -maxDelta) {
            delta = -maxDelta;
        }
        const next = sdfSlice[i] + delta;
        out[i] = next <= 0 ? 0 : next >= 255 ? 255 : (next + 0.5) | 0;
    }
    return out;
}

/**
 * Result of {@link buildIntensityConfidenceMap}: a per-pixel
 * confidence scalar in `[0, 1]` plus the cached intensity gradient
 * components used to compute it. Refinement consumes these directly
 * so the gradient never needs to be recomputed.
 *
 * Pixels with `confidence === 0` are guaranteed to have no usable
 * edge evidence — either the gradient was below the noise floor,
 * a neighbour intensity sample was non-finite (out-of-volume /
 * masked), or the pixel sat on the slice boundary where central
 * differences are undefined. Refinement skips them outright.
 */
export type IntensityConfidenceMap = {
    /** Per-pixel confidence in `[0, 1]`, row-major. */
    confidence: Float32Array;
    /** ∂I/∂x via central differences, row-major. */
    gradientX: Float32Array;
    /** ∂I/∂y via central differences, row-major. */
    gradientY: Float32Array;
    width: number;
    height: number;
};

/**
 * Build a per-pixel confidence map from an intensity slice. The
 * score is derived purely from the local intensity gradient
 * magnitude:
 *
 *   - `|∇I| < edgeNoiseFloor` → 0 (signal indistinguishable from
 *     noise, no anatomical edge evidence).
 *   - `|∇I| ≥ edgeStrengthMax` → 1 (saturated, very strong edge).
 *   - In between, the score ramps linearly.
 *
 * Boundary pixels (the outer 1-pixel ring) are left at 0 since
 * central differences are undefined there. Pixels with any
 * non-finite intensity neighbour (out-of-volume / masked) are also
 * left at 0 so the refinement step skips them automatically.
 *
 * Confidence is intentionally a property of the IMAGE alone:
 * directional alignment with the SDF being refined is applied
 * later, in {@link refineSliceWithIntensityEdge}, so the same map
 * could be reused across multiple correction stages or
 * visualisations.
 *
 * Single-pass, allocation-free inside the inner loop.
 */
export function buildIntensityConfidenceMap(
    intensitySlice: ArrayLike<number>,
    width: number,
    height: number,
    options: Partial<EdgeRefinementOptions> = {}
): IntensityConfidenceMap {
    if (intensitySlice.length !== width * height) {
        throw new Error(
            `buildIntensityConfidenceMap: intensitySlice length ${intensitySlice.length} does not match ${width}x${height}`
        );
    }
    const opts = { ...DEFAULT_EDGE_REFINEMENT_OPTIONS, ...options };
    const edgeRange = Math.max(
        1e-6,
        opts.edgeStrengthMax - opts.edgeNoiseFloor
    );

    const total = width * height;
    const confidence = new Float32Array(total);
    const gradientX = new Float32Array(total);
    const gradientY = new Float32Array(total);

    if (width < 3 || height < 3) {
        return { confidence, gradientX, gradientY, width, height };
    }

    // Reused scratch for the per-pixel gradient (gx, gy) — keeps the
    // inner loop allocation-free.
    const grad = new Float32Array(2);
    for (let y = 1; y < height - 1; y++) {
        const rowBase = y * width;
        for (let x = 1; x < width - 1; x++) {
            const idx = rowBase + x;
            const iW = intensitySlice[idx - 1];
            const iE = intensitySlice[idx + 1];
            const iN = intensitySlice[idx - width];
            const iS = intensitySlice[idx + width];
            if (
                !Number.isFinite(iW) ||
                !Number.isFinite(iE) ||
                !Number.isFinite(iN) ||
                !Number.isFinite(iS)
            ) {
                continue;
            }
            const mag = centralDifferenceGradient2D(
                intensitySlice,
                idx,
                width,
                grad
            );
            if (mag < opts.edgeNoiseFloor) {
                continue;
            }
            gradientX[idx] = grad[0];
            gradientY[idx] = grad[1];
            const score = (mag - opts.edgeNoiseFloor) / edgeRange;
            confidence[idx] = Math.min(1, score);
        }
    }

    return { confidence, gradientX, gradientY, width, height };
}

/**
 * Confidence-aware local refinement of an interpolated SDF slice.
 *
 * The B-spline blend gives smooth shape continuity along Z; this
 * pass adds local boundary alignment by gently pulling the iso
 * toward intensity edges that the {@link buildIntensityConfidenceMap
 * confidence map} marks as anatomically plausible. It is a
 * refinement layer — never a re-segmentation:
 *
 *   - Operates ONLY in a narrow band of width `bandWidth` around
 *     the iso (`|sdf − threshold| < bandWidth`). Deep interior /
 *     exterior pixels are not touched, so topology is preserved.
 *   - Per-pixel correction weight is the product of three factors,
 *     all in `[0, 1]`:
 *       1. `confidenceMap.confidence[idx]` — image-derived edge
 *          evidence.
 *       2. `|cos(∇I, ∇SDF)|` — directional alignment. High when
 *          an intensity edge crosses the iso roughly perpendicular
 *          to the iso-tangent (i.e. it IS our boundary). Low when
 *          the edge runs parallel (likely a neighbouring structure
 *          we don't want to be pulled toward).
 *       3. `proximity` — 1 at the iso, 0 at the band edge.
 *   - Per-pixel delta is clamped to `±maxDelta` so a single noisy
 *     pixel cannot flip topology.
 *   - "Snap toward threshold" formulation handles both expansion
 *     and contraction: pixels just inside the iso with strong
 *     edge evidence are pulled outward (their SDF drops toward
 *     128, the iso passes them); pixels just outside are pulled
 *     inward (their SDF rises toward 128).
 *
 * Stability:
 *   - All neighbour reads come from the INPUT slice, so the pass
 *     is deterministic and order-independent.
 *   - Output values clamped to `[0, 255]`.
 *
 * Performance: O(width × height), single forward pass, allocation
 * is one output buffer plus the confidence map. Inner loop has no
 * allocations. Boundary pixels are skipped (central differences
 * need neighbours on all sides).
 *
 * Fallback: if `intensitySlice` is `undefined` we return a copy
 * of the input SDF so callers can chain unconditionally.
 *
 * @param sdfBytesPerMm SDF byte distance units per physical mm for the
 *   SDF being refined, from {@link ContourTexture.sdfBytesPerMm}. Used
 *   to convert the mm spatial options into byte units; required so the
 *   conversion reflects the SDF's actual encoding (the texture's own
 *   margin) rather than a fixed default.
 */
export function refineSliceWithIntensityEdge(
    sdfSlice: Uint8Array,
    intensitySlice: ArrayLike<number> | undefined,
    width: number,
    height: number,
    sdfBytesPerMm: number,
    options: Partial<EdgeRefinementOptions> = {}
): Uint8Array {
    const out = new Uint8Array(sdfSlice);
    if (!intensitySlice) {
        return out;
    }
    if (sdfSlice.length !== width * height) {
        throw new Error(
            `refineSliceWithIntensityEdge: sdfSlice length ${sdfSlice.length} does not match ${width}x${height}`
        );
    }
    if (intensitySlice.length !== width * height) {
        throw new Error(
            `refineSliceWithIntensityEdge: intensitySlice length ${intensitySlice.length} does not match ${width}x${height}`
        );
    }
    if (width < 3 || height < 3) {
        return out;
    }
    if (sdfBytesPerMm <= 0) {
        throw new Error(
            `refineSliceWithIntensityEdge: sdfBytesPerMm must be positive, got ${sdfBytesPerMm}`
        );
    }

    const map = buildIntensityConfidenceMap(
        intensitySlice,
        width,
        height,
        options
    );
    return applyIntensityEdgeRefinement(
        sdfSlice,
        out,
        map,
        sdfBytesPerMm,
        options
    );
}

/**
 * Apply intensity-guided edge refinement using a precomputed
 * confidence map. `out` is mutated in place; `sdfSlice` is
 * read-only.
 */
function applyIntensityEdgeRefinement(
    sdfSlice: Uint8Array,
    out: Uint8Array,
    map: IntensityConfidenceMap,
    sdfBytesPerMm: number,
    options: Partial<EdgeRefinementOptions> = {}
): Uint8Array {
    const { width, height } = map;
    if (sdfSlice.length !== width * height || out.length !== width * height) {
        throw new Error(
            `applyIntensityEdgeRefinement: SDF buffer length does not match confidence map ${width}x${height}`
        );
    }
    if (width < 3 || height < 3) {
        return out;
    }
    const opts = { ...DEFAULT_EDGE_REFINEMENT_OPTIONS, ...options };
    if (opts.bandWidthMm <= 0 || opts.maxShiftMm <= 0 || opts.alpha <= 0) {
        throw new Error(
            'applyIntensityEdgeRefinement: bandWidthMm, maxShiftMm, and alpha must be positive'
        );
    }
    if (opts.edgeStrengthMax <= opts.edgeNoiseFloor) {
        throw new Error(
            'applyIntensityEdgeRefinement: edgeStrengthMax must be greater than edgeNoiseFloor'
        );
    }
    const { threshold, alpha } = opts;
    // Convert the mm spatial knobs into the SDF's byte units using the
    // scale the caller derived from the texture's own margin.
    const bandWidth = opts.bandWidthMm * sdfBytesPerMm;
    const maxDelta = opts.maxShiftMm * sdfBytesPerMm;
    // Tiny floor guarding the two gradient-magnitude divisions below:
    // the SDF gradient (used to define the local boundary direction)
    // and the intensity gradient (the alignment-cosine denominator).
    // Not specific to either field — it just keeps the cosine
    // well-defined when a gradient is degenerate.
    const epsilon = 1e-3;

    const conf = map.confidence;
    const intGx = map.gradientX;
    const intGy = map.gradientY;

    const deltaField = new Float32Array(width * height);
    const eligible = new Uint8Array(width * height);
    // Reused scratch for the per-pixel SDF gradient (gx, gy).
    const sdfGrad = new Float32Array(2);
    for (let y = 1; y < height - 1; y++) {
        const rowBase = y * width;
        for (let x = 1; x < width - 1; x++) {
            const idx = rowBase + x;
            const c = conf[idx];
            if (c <= 0) {
                continue; // no edge evidence here
            }

            const dist = sdfSlice[idx];
            const distToIso = Math.abs(dist - threshold);
            if (distToIso >= bandWidth) {
                continue;
            }

            const sdfMag = centralDifferenceGradient2D(
                sdfSlice,
                idx,
                width,
                sdfGrad
            );
            if (sdfMag < epsilon) {
                // Flat SDF — direction undefined; can't align safely.
                continue;
            }
            const sdfGx = sdfGrad[0];
            const sdfGy = sdfGrad[1];

            const gx = intGx[idx];
            const gy = intGy[idx];
            const iMag = Math.sqrt(gx * gx + gy * gy);
            if (iMag < epsilon) {
                // Defensive divide-by-zero guard for the alignment
                // cosine below. In practice unreachable: we already
                // skipped pixels with zero confidence, and a non-zero
                // confidence implies |∇I| ≥ edgeNoiseFloor ≫ epsilon.
                continue;
            }

            const cosAlign = (gx * sdfGx + gy * sdfGy) / (iMag * sdfMag);
            const alignmentFactor = Math.abs(cosAlign);

            const proximity = 1 - distToIso / bandWidth;
            const weight = alpha * c * alignmentFactor * proximity;

            deltaField[idx] = weight * (threshold - dist);
            eligible[idx] = 1;
        }
    }

    return commitDeltaField(sdfSlice, out, deltaField, eligible, maxDelta);
}

// ─── Region-based intensity refinement ────────────────────────────────────────

/**
 * Tuning parameters for {@link refineSliceWithIntensityRegion}.
 * This pass complements the gradient-based
 * {@link refineSliceWithIntensityEdge} by using **absolute
 * intensity values** to decide whether each boundary pixel belongs
 * inside or outside the structure.
 *
 * Ideal for structures with strong interior/exterior intensity
 * contrast such as lungs on CT (air ≈ −800 HU vs tissue ≈ +30),
 * hot lesions on PET, or any region with clear contrast.
 */
export type RegionRefinementOptions = {
    /** SDF iso-value (default {@link SDF_ISO_THRESHOLD}). */
    threshold: number;
    /**
     * Half-width of the narrow band around the iso-surface to correct,
     * in **millimetres**. Pixels farther from the iso are untouched. A
     * wider band reaches further misplaced pixels but risks modifying
     * correctly placed ones. Converted to byte units using the
     * `sdfBytesPerMm` argument.
     */
    bandWidthMm: number;
    /**
     * Physical distance (**mm**) from the iso beyond which a pixel is
     * considered "deep" interior or exterior. These deep pixels are
     * sampled to compute the characteristic intensity of each region.
     * Converted to byte units using the `sdfBytesPerMm` argument.
     */
    sampleDepthMm: number;
    /**
     * Minimum number of deep-interior **and** deep-exterior pixels
     * required. If either population is smaller, refinement is
     * skipped — the statistics would be unreliable.
     */
    minSampleCount: number;
    /**
     * Minimum |meanInterior − meanExterior| intensity contrast
     * required to activate. Below this the two regions are too
     * similar to distinguish reliably. Default 200, tuned for CT
     * (easily met by air/tissue interfaces at ∼800+ HU gap). For
     * other modalities, scale via
     * {@link scaleRefinementOptionsForSpread}.
     */
    minRegionContrast: number;
    /**
     * Correction strength in [0, 1]. Multiplied with the region
     * disagreement score and proximity to produce the final delta.
     */
    alpha: number;
    /**
     * Maximum iso displacement per pixel per pass, in **millimetres**
     * (converted to byte units using the `sdfBytesPerMm` argument).
     * Because the
     * per-pixel delta is `alpha · regionScore · proximity · maxShift`,
     * the *effective* maximum push is ≈ `alpha × maxShift` (the clamp
     * rarely binds). Each band pixel is classified independently from
     * (noisy) absolute intensity, so a large cap lets neighbouring
     * pixels move by very different amounts and the boundary turns
     * wavy / distorted — keep the effective push gentle (the default
     * ≈ 0.4 mm with `alpha = 0.6` gives an effective ~0.25 mm).
     */
    maxShiftMm: number;
};

export const DEFAULT_REGION_REFINEMENT_OPTIONS: Readonly<RegionRefinementOptions> =
    {
        threshold: SDF_ISO_THRESHOLD,
        // ≈ the previously hand-tuned 40 / 50 / 5 byte values at the
        // default 10 mm falloff.
        bandWidthMm: 3.1,
        sampleDepthMm: 3.9,
        minSampleCount: 20,
        minRegionContrast: 200,
        alpha: 0.6,
        maxShiftMm: 0.4,
    };

/**
 * Characteristic intensity statistics for the deep interior /
 * exterior populations of the interpolated SDF. Each region is
 * modelled as a Gaussian (mean + std) rather than a single centroid,
 * so the classification in {@link refineSliceWithIntensityRegion} can
 * account for how spread-out each population is — important for
 * heterogeneous structures (e.g. mixed tissue, variable PET uptake)
 * where a lone mean is a poor descriptor.
 *
 * Deliberately not built on `domain/statistics/summaryStatistics`: that
 * accumulator is single-pass Welford, and swapping the numerics of a
 * refinement pass belongs in its own change with its own verification.
 */
export type RegionIntensityStats = {
    meanInterior: number;
    meanExterior: number;
    /** Population standard deviation (÷N) of the deep interior. */
    stdInterior: number;
    /** Population standard deviation (÷N) of the deep exterior. */
    stdExterior: number;
};

export function computeRegionIntensityStats(
    sdfSlice: Uint8Array,
    intensitySlice: ArrayLike<number>,
    sdfBytesPerMm: number,
    options: Partial<RegionRefinementOptions> = {}
): RegionIntensityStats | undefined {
    if (sdfBytesPerMm <= 0) {
        throw new Error(
            `computeRegionIntensityStats: sdfBytesPerMm must be positive, got ${sdfBytesPerMm}`
        );
    }
    const opts = { ...DEFAULT_REGION_REFINEMENT_OPTIONS, ...options };
    if (opts.sampleDepthMm <= 0 || opts.minSampleCount <= 0) {
        throw new Error(
            'computeRegionIntensityStats: sampleDepthMm and minSampleCount must be positive'
        );
    }
    // Deep-sample threshold in the SDF's byte units.
    const sampleDepth = opts.sampleDepthMm * sdfBytesPerMm;

    // Pass 1 — accumulate means of the deep-interior / deep-exterior
    // populations. A deep pixel is one at least `sampleDepth` past the
    // iso on its side; non-finite samples (out-of-volume) are skipped.
    let sumInterior = 0;
    let countInterior = 0;
    let sumExterior = 0;
    let countExterior = 0;

    for (let i = 0; i < sdfSlice.length; i++) {
        const v = intensitySlice[i];
        if (!Number.isFinite(v)) {
            continue;
        }
        const dist = sdfSlice[i] - opts.threshold;
        if (dist >= sampleDepth) {
            sumInterior += v;
            countInterior++;
        } else if (dist <= -sampleDepth) {
            sumExterior += v;
            countExterior++;
        }
    }

    if (
        countInterior < opts.minSampleCount ||
        countExterior < opts.minSampleCount
    ) {
        return undefined;
    }

    const meanInterior = sumInterior / countInterior;
    const meanExterior = sumExterior / countExterior;

    if (Math.abs(meanInterior - meanExterior) < opts.minRegionContrast) {
        return undefined;
    }

    // Pass 2 — accumulate squared deviations from each population's
    // mean. The two-pass form (Σ(vᵢ − mean)²) is numerically stable;
    // the single-pass E[x²] − E[x]² shortcut suffers catastrophic
    // cancellation for large intensities (e.g. HU ≈ ±1000) and can go
    // slightly negative, which is why it needed a `max(0, …)` floor.
    let sumSqDevInterior = 0;
    let sumSqDevExterior = 0;

    for (let i = 0; i < sdfSlice.length; i++) {
        const v = intensitySlice[i];
        if (!Number.isFinite(v)) {
            continue;
        }
        const dist = sdfSlice[i] - opts.threshold;
        if (dist >= sampleDepth) {
            const dv = v - meanInterior;
            sumSqDevInterior += dv * dv;
        } else if (dist <= -sampleDepth) {
            const dv = v - meanExterior;
            sumSqDevExterior += dv * dv;
        }
    }

    return {
        meanInterior,
        meanExterior,
        stdInterior: Math.sqrt(sumSqDevInterior / countInterior),
        stdExterior: Math.sqrt(sumSqDevExterior / countExterior),
    };
}

/**
 * Region-based intensity refinement of an interpolated SDF slice.
 *
 * Where {@link refineSliceWithIntensityEdge} uses intensity
 * **gradients** (edges) to nudge the iso-surface, this pass uses
 * the **absolute intensity value** at each boundary pixel to
 * determine whether it belongs inside or outside the structure,
 * then pushes the SDF accordingly.
 *
 * Algorithm:
 *   1. Sample deep-interior and deep-exterior pixels to model each
 *      region as a Gaussian (mean + std) — not just a centroid.
 *   2. For each pixel in the narrow band around the iso:
 *      a. Compute a membership score in [−1, +1] from the squared
 *         **Mahalanobis** distances (distance-to-mean normalised by
 *         each region's own std):
 *         `(exteriorDist² − interiorDist²) / (exteriorDist² + interiorDist²)`.
 *         Score +1 → pixel matches the interior distribution; −1 →
 *         the exterior distribution. Variance-awareness makes the
 *         classifier robust to heterogeneous regions (a wide-spread
 *         population tolerates outliers instead of mislabelling them).
 *      b. If the score **disagrees** with the pixel's current SDF
 *         side (inside vs outside), apply a correction:
 *         - inside contour but exterior intensity → decrease SDF
 *           (contract)
 *         - outside contour but interior intensity → increase SDF
 *           (expand)
 *      c. The delta is scaled by `alpha × |score| × proximity`
 *         and clamped to `±maxDelta`.
 *
 * Self-adapting: the interior/exterior Gaussians are derived from the
 * current SDF, so the pass works for any structure with sufficient
 * contrast — no per-organ thresholds needed. For a homogeneous region
 * the std floor cancels in the score ratio, reducing the classifier to
 * the simpler mean-only form.
 *
 * Performance: two passes over the pixel array (statistics +
 * correction). Deterministic. Single output allocation.
 *
 * Fallback: returns a copy of the input when `intensitySlice` is
 * undefined, contrast is insufficient, or sample populations are
 * too small.
 *
 * @param sdfBytesPerMm SDF byte distance units per physical mm for the
 *   SDF being refined, from {@link ContourTexture.sdfBytesPerMm}. Used
 *   to convert the mm spatial options into byte units; required so the
 *   conversion reflects the SDF's actual encoding (the texture's own
 *   margin) rather than a fixed default.
 */
export function refineSliceWithIntensityRegion(
    sdfSlice: Uint8Array,
    intensitySlice: ArrayLike<number> | undefined,
    width: number,
    height: number,
    sdfBytesPerMm: number,
    options: Partial<RegionRefinementOptions> = {}
): Uint8Array {
    const out = new Uint8Array(sdfSlice);
    if (!intensitySlice) {
        return out;
    }
    if (sdfSlice.length !== width * height) {
        throw new Error(
            `refineSliceWithIntensityRegion: sdfSlice length ${sdfSlice.length} does not match ${width}x${height}`
        );
    }
    if (intensitySlice.length !== width * height) {
        throw new Error(
            `refineSliceWithIntensityRegion: intensitySlice length ${intensitySlice.length} does not match ${width}x${height}`
        );
    }
    if (sdfBytesPerMm <= 0) {
        throw new Error(
            `refineSliceWithIntensityRegion: sdfBytesPerMm must be positive, got ${sdfBytesPerMm}`
        );
    }

    const opts = { ...DEFAULT_REGION_REFINEMENT_OPTIONS, ...options };
    if (
        opts.bandWidthMm <= 0 ||
        opts.maxShiftMm <= 0 ||
        opts.alpha <= 0 ||
        opts.sampleDepthMm <= 0 ||
        opts.minSampleCount <= 0
    ) {
        throw new Error(
            'refineSliceWithIntensityRegion: bandWidthMm, maxShiftMm, alpha, sampleDepthMm, and minSampleCount must be positive'
        );
    }
    // Convert the mm spatial knobs into the SDF's byte units using the
    // scale the caller derived from the texture's own margin.
    const bandWidth = opts.bandWidthMm * sdfBytesPerMm;
    const maxDelta = opts.maxShiftMm * sdfBytesPerMm;

    const stats = computeRegionIntensityStats(
        sdfSlice,
        intensitySlice,
        sdfBytesPerMm,
        options
    );
    if (!stats) {
        return out;
    }

    const { meanInterior, meanExterior, stdInterior, stdExterior } = stats;

    // Variance-aware (Mahalanobis) classification: normalise each
    // pixel's distance-to-mean by that region's own spread, so a
    // heterogeneous region (large std) is more tolerant and a tight
    // region is stricter. Std is floored relative to the inter-mean
    // contrast so a perfectly homogeneous population doesn't divide by
    // zero; in that homogeneous case the floor cancels in the score
    // ratio and the behaviour reduces exactly to the previous
    // mean-only classification.
    const stdFloor = Math.max(
        1e-6,
        1e-3 * Math.abs(meanInterior - meanExterior)
    );
    const sigmaInterior = Math.max(stdInterior, stdFloor);
    const sigmaExterior = Math.max(stdExterior, stdFloor);

    const deltaField = new Float32Array(sdfSlice.length);
    const eligible = new Uint8Array(sdfSlice.length);
    for (let i = 0; i < sdfSlice.length; i++) {
        const sdf = sdfSlice[i];
        const distToIso = Math.abs(sdf - opts.threshold);
        if (distToIso >= bandWidth) {
            continue;
        }

        const v = intensitySlice[i];
        if (!Number.isFinite(v)) {
            continue;
        }

        // Squared Mahalanobis distances to each region's Gaussian.
        const zInterior = (v - meanInterior) / sigmaInterior;
        const zExterior = (v - meanExterior) / sigmaExterior;
        const interiorDist2 = zInterior * zInterior;
        const exteriorDist2 = zExterior * zExterior;
        const totalDist2 = interiorDist2 + exteriorDist2;
        if (totalDist2 < 1e-6) {
            continue;
        }

        // +1 = clearly interior, −1 = clearly exterior
        const regionScore = (exteriorDist2 - interiorDist2) / totalDist2;

        const isInside = sdf >= opts.threshold;
        const proximity = 1 - distToIso / bandWidth;

        // Only correct where the intensity evidence disagrees with
        // the current side: inside-contour-but-exterior-intensity →
        // contract; outside-contour-but-interior-intensity → expand.
        if ((isInside && regionScore < 0) || (!isInside && regionScore > 0)) {
            deltaField[i] = opts.alpha * regionScore * proximity * maxDelta;
            eligible[i] = 1;
        }
    }

    return commitDeltaField(sdfSlice, out, deltaField, eligible, maxDelta);
}

// ─── Intensity slice extraction ────────────────────────────────────────────────────────────

/**
 * Extract a 2D intensity slice aligned with a structure's SDF texture
 * grid at the given target Z, sampling from an image's float pixel
 * buffer.
 *
 * Returns `undefined` when the texture grid is not voxel-aligned with
 * the image grid — a direct row-by-row copy would otherwise yield
 * meaningless values — or when the target Z falls outside the image
 * volume. "Voxel-aligned" requires BOTH the same sampling (identical
 * orientation + resolution, see `hasSameSamplingAs`) AND a
 * sub-voxel-exact translational offset; differing orientation or
 * spacing (e.g. registered / oblique images) is rejected.
 *
 * Texture-grid pixels that lie outside the image volume in X / Y are
 * filled with `NaN`, which the refinement passes treat as "missing"
 * and skip.
 *
 * @param textureGrid Grid of the structure SDF texture being refined.
 * @param imageGrid Grid of the source image. Pass the un-transformed
 *        parent image grid to avoid any registration transform on
 *        secondary images.
 * @param floatPixelData Row-major float intensity volume aligned with
 *        {@link imageGrid} (e.g. `Image3D.floatPixelData`).
 * @param targetVoxelZ Texture-grid Z index of the slice to extract.
 * @param alignmentTolerance Max sub-voxel offset (in voxels) tolerated
 *        between the two grids before extraction is refused. Default
 *        0.05.
 */
export function extractIntensitySliceAlignedToGrid(
    textureGrid: Grid,
    imageGrid: Grid,
    floatPixelData: ArrayLike<number>,
    targetVoxelZ: number,
    alignmentTolerance = 0.05
): Float32Array | undefined {
    // Voxel-index remapping by a constant offset is only meaningful
    // when both grids sample space the same way. Without this an image
    // with a different orientation or spacing could still pass the
    // integer-offset check below and yield a geometrically wrong
    // row-by-row copy.
    const imageVoxelOfTextureCorner = textureGrid.originInVoxelsOf(imageGrid);
    if (imageVoxelOfTextureCorner === undefined) {
        return undefined;
    }

    // Voxel-aligned grids map the texture's voxel indices directly to
    // the image's via a constant integer offset.
    const offsetX = Math.round(imageVoxelOfTextureCorner[0]);
    const offsetY = Math.round(imageVoxelOfTextureCorner[1]);
    const offsetZ = Math.round(imageVoxelOfTextureCorner[2]);
    if (
        Math.abs(imageVoxelOfTextureCorner[0] - offsetX) > alignmentTolerance ||
        Math.abs(imageVoxelOfTextureCorner[1] - offsetY) > alignmentTolerance ||
        Math.abs(imageVoxelOfTextureCorner[2] - offsetZ) > alignmentTolerance
    ) {
        return undefined;
    }

    const imageZ = targetVoxelZ + offsetZ;
    if (imageZ < 0 || imageZ >= imageGrid.sizeZ) {
        return undefined;
    }

    const sx = textureGrid.sizeX;
    const sy = textureGrid.sizeY;
    const ix = imageGrid.sizeX;
    const iy = imageGrid.sizeY;
    const sliceStride = ix * iy;
    const sliceBaseIdx = imageZ * sliceStride;

    const out = new Float32Array(sx * sy);
    // Default to NaN ("no data") so texture-grid pixels that fall
    // outside the image volume in X/Y are treated as missing by the
    // refinement passes (which skip non-finite samples). Zero would
    // be incorrect — it's a valid intensity value (e.g. 0 HU = water
    // on CT) and would bias the region classifier.
    out.fill(NaN);

    for (let y = 0; y < sy; y++) {
        const imageY = y + offsetY;
        if (imageY < 0 || imageY >= iy) {
            continue;
        }
        const rowBase = sliceBaseIdx + imageY * ix;
        const outRowBase = y * sx;
        for (let x = 0; x < sx; x++) {
            const imageX = x + offsetX;
            if (imageX < 0 || imageX >= ix) {
                continue;
            }
            out[outRowBase + x] = floatPixelData[rowBase + imageX];
        }
    }

    return out;
}

/**
 * Robust intensity spread (P95 − P5) of the finite samples in an
 * intensity slice. Used to scale the intensity-unit refinement
 * thresholds (which are tuned for CT Hounsfield Units by default)
 * to another modality's intensity range via
 * {@link scaleRefinementOptionsForSpread}.
 *
 * Because the value is a per-slice spread, it is unit-agnostic: a
 * PET slice expressed in raw activity vs SUV (a constant factor
 * apart) yields proportionally scaled thresholds, so the refinement
 * behaves identically either way.
 *
 * Returns `undefined` when there are too few finite samples to
 * characterise the slice or the spread is degenerate (flat slice),
 * letting the caller skip refinement rather than chase noise.
 */
export function estimateRobustIntensitySpread(
    intensitySlice: ArrayLike<number>
): number | undefined {
    const finite: number[] = [];
    for (let i = 0; i < intensitySlice.length; i++) {
        const v = intensitySlice[i];
        if (Number.isFinite(v)) {
            finite.push(v);
        }
    }
    if (finite.length < 16) {
        return undefined;
    }
    finite.sort((a, b) => a - b);
    const at = (q: number): number => {
        const idx = Math.round(q * (finite.length - 1));
        return finite[Math.min(finite.length - 1, Math.max(0, idx))];
    };
    const spread = at(0.95) - at(0.05);
    return spread > 1e-6 ? spread : undefined;
}

// ─── Spread-based option scaling ──────────────────────────────────────────────

/**
 * Robust intensity spread (P95 − P5) of a typical CT slice, in
 * Hounsfield Units. The default edge and region refinement
 * parameters are tuned for this spread. When refining non-CT images,
 * scale the intensity-unit parameters by
 * `imageSpread / REFERENCE_INTENSITY_SPREAD` so the refinement's
 * sensitivity maps correctly onto the modality's own intensity range.
 */
export const REFERENCE_INTENSITY_SPREAD = 1500;

/**
 * Scale the intensity-unit parameters of the default edge and region
 * refinement options to match a given image intensity spread. The
 * spatial parameters (`bandWidthMm`, `maxShiftMm`, `sampleDepthMm`)
 * are NOT scaled — they are intensity-independent.
 *
 * Usage:
 * ```ts
 * const spread = estimateRobustIntensitySpread(intensitySlice);
 * if (spread !== undefined) {
 *     const opts = scaleRefinementOptionsForSpread(spread);
 *     refineSliceWithIntensityEdge(sdf, intensitySlice, w, h, opts.edge);
 *     refineSliceWithIntensityRegion(sdf, intensitySlice, w, h, opts.region);
 * }
 * ```
 */
export function scaleRefinementOptionsForSpread(spread: number): {
    edge: Partial<EdgeRefinementOptions>;
    region: Partial<RegionRefinementOptions>;
} {
    const scale = spread / REFERENCE_INTENSITY_SPREAD;
    return {
        edge: {
            edgeNoiseFloor:
                DEFAULT_EDGE_REFINEMENT_OPTIONS.edgeNoiseFloor * scale,
            edgeStrengthMax:
                DEFAULT_EDGE_REFINEMENT_OPTIONS.edgeStrengthMax * scale,
        },
        region: {
            minRegionContrast:
                DEFAULT_REGION_REFINEMENT_OPTIONS.minRegionContrast * scale,
        },
    };
}
